import { useState } from 'react';
import { PlatformRole } from 'minka-shared';
import { Button, TabSwitch } from '@/components/common';
import { useAppContext } from '@/hooks/useAppContext';
import { FaArrowLeft, FaLock, FaShieldAlt } from 'react-icons/fa';
import { IslandsTab } from './IslandsTab';
import { UsersTab } from './UsersTab';

type ConsoleTab = 'users' | 'islands';

interface PlatformAdminViewProps {
  /** Leaves the console and returns to the island list. */
  onClose: () => void;
}

/**
 * The superadmin console (§8): a full-screen view with the two tabs of §4.3,
 * Users and Islands.
 *
 * The gate is here rather than only at the entry points. The entry points hide
 * themselves from ordinary users, but hiding a button is a courtesy, not a
 * control: anything that puts this view on screen for somebody who is not a
 * superadmin gets the refusal instead. The server refuses every `/admin/*` call
 * on its own account regardless — this is what the person sees.
 */
export function PlatformAdminView({ onClose }: PlatformAdminViewProps) {
  const { auth, platformAdmin } = useAppContext();
  const [tab, setTab] = useState<ConsoleTab>('users');

  const isSuperadmin = auth.user?.platformRole === PlatformRole.SUPERADMIN;

  if (!isSuperadmin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-game-navy via-game-dark to-game-medium text-gray-100 flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <FaLock className="text-game-golden text-4xl mx-auto" />
          <h1 className="text-2xl font-bold text-white">You don&apos;t have access to this area</h1>
          <p className="text-gray-400 leading-relaxed">
            The platform console is for superadmins. Everything you can do lives on your islands.
          </p>
          <div className="flex justify-center">
            <Button variant="secondary" onClick={onClose}>
              <FaArrowLeft className="text-sm" />
              Back to islands
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-game-navy via-game-dark to-game-medium text-gray-100 overflow-auto">
      <div className="sticky top-0 z-40 bg-gradient-to-b from-game-dark via-game-dark/95 to-game-dark/90 backdrop-blur-md border-b border-game-medium/30 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <div className="flex items-center gap-3">
              <FaShieldAlt className="text-game-golden" />
              <h1 className="text-lg font-bold text-white">Platform admin</h1>
            </div>
            <Button variant="secondary" onClick={onClose} className="px-3 h-9 text-sm">
              <FaArrowLeft className="text-sm" />
              <span className="hidden md:inline font-medium">Back to islands</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex gap-2 p-1 bg-game-medium/20 rounded-lg max-w-sm">
          <TabSwitch isActive={tab === 'users'} onClick={() => setTab('users')}>
            Users
          </TabSwitch>
          <TabSwitch isActive={tab === 'islands'} onClick={() => setTab('islands')}>
            Islands
          </TabSwitch>
        </div>

        {/* Presence waits for the island the operator came from to have been
            left; asking sooner shows them standing on it (§6.7). */}
        {tab === 'users' ? (
          <UsersTab currentUserId={auth.user?.id} presenceReady={platformAdmin.isReady} />
        ) : (
          <IslandsTab presenceReady={platformAdmin.isReady} />
        )}
      </div>
    </div>
  );
}
